// client/src/utils/writeOutbox.js
// Offline queue for the app's substantive writes — marks, attendance, admissions.
//
// Pairs with the server's Idempotency-Key middleware. Each queued write carries a
// key generated once at enqueue time and reused on every retry, so the dangerous
// case (server committed, response lost, client retries) collapses to a replay of
// the original response instead of a duplicate mark or a duplicate admission.
//
// Records are also pinned to the user and the school/year/phase scope in effect
// when they were queued — see outboxIdentity for why.

import { fetchJson } from './fetchJson';
import { enqueue, getAll, remove, update, isSupported } from './outbox';
import { currentIdentityId, captureScope, newIdempotencyKey } from './outboxIdentity';
import { OFFLINE_ID_PREFIX, isOfflineId, idInPath } from './offlineOverlay';

const TYPE = 'write';
const MAX_TRIES = 5;

// Write kinds the queue understands. `label` is what the UI shows the user.
export const WRITE_KINDS = {
  MARKS: 'marks',
  ATTENDANCE: 'attendance',
  ADMISSION: 'admission',
  // The general one: any add, edit or delete made offline. The named kinds
  // predate it and are kept because "3 marks waiting" reads better than
  // "3 changes waiting".
  CHANGE: 'change',
};

export function writeOutboxSupported() {
  return isSupported();
}

/**
 * Queue a write for delivery when connectivity returns.
 *
 * @param {object}  args
 * @param {string}  args.kind      one of WRITE_KINDS
 * @param {string}  args.endpoint  API path, e.g. '/api/marks/bulk-upsert'
 * @param {string}  [args.method]  defaults to POST
 * @param {object}  args.body      JSON payload
 * @param {string}  [args.label]   human description, shown in the pending list
 * @param {string}  [args.idempotencyKey] reuse the key from a failed online attempt
 *                  — see sendOrQueue; minting a fresh one would defeat deduplication.
 * @param {object}  [args.scope]   scope snapshot taken before the online attempt
 */
export async function queueWrite({ kind, endpoint, method = 'POST', body, label, idempotencyKey, scope }) {
  return enqueue({
    type: TYPE,
    kind,
    ownerId: currentIdentityId(),
    idempotencyKey: idempotencyKey || newIdempotencyKey(),
    scope: scope || captureScope(),
    payload: { endpoint, method, body, label: label || '' },
  });
}

// Pending writes belonging to the current user, oldest first. Scoped by owner so
// one user's queued work is never shown to the next user of a shared device.
export async function getQueuedWrites(kind) {
  try {
    const me = currentIdentityId();
    if (!me) return [];
    return (await getAll(TYPE))
      .filter((r) => r.ownerId === me && (!kind || r.kind === kind))
      .map((r) => ({
        outboxId: r.id,
        kind: r.kind,
        label: r.payload?.label || '',
        endpoint: r.payload?.endpoint || '',
        createdAt: r.createdAt,
        tries: r.tries || 0,
        lastError: r.lastError || '',
      }));
  } catch (_) {
    return [];
  }
}

// How many writes the current user still has queued (for badges/banners).
export async function pendingWriteCount(kind) {
  return (await getQueuedWrites(kind)).length;
}

/**
 * The queue in the shape the read overlay needs, oldest first.
 *
 * Separate from getQueuedWrites, which is for DISPLAY and hides the body; the
 * overlay needs the body in order to show the row.
 */
export async function pendingOverlayWrites() {
  try {
    const me = currentIdentityId();
    if (!me) return [];
    return (await getAll(TYPE))
      .filter((r) => r.ownerId === me)
      .map((r) => ({
        outboxId: r.id,
        method: r.payload?.method || 'POST',
        endpoint: r.payload?.endpoint || '',
        body: r.payload?.body ?? null,
      }))
      .filter((w) => w.endpoint);
  } catch (_) {
    return [];
  }
}

/**
 * Point queued writes at the real id a create has just been given.
 *
 * WITHOUT THIS THE QUEUE CORRUPTS ITSELF. Create a class offline and the row
 * gets a temporary id; edit it before reconnecting and the edit is queued
 * against `/api/classes/offline:7`. When the create reaches the server the
 * class becomes id 84 — and the edit, still addressed to `offline:7`, would 404
 * forever and be dropped after five tries, silently losing it.
 */
async function remapOfflineId(tempId, realId, inFlight = null) {
  if (!tempId || realId == null) return;
  // The records still to be processed IN THIS PASS, so the rewrite is visible
  // to the loop that is running. Without this the remap reaches storage but not
  // the stale copies the loop is already holding, and the dependent write sits
  // queued until some later flush — the user reconnects, watches the counter
  // drop to one, and it stays there.
  const rows = inFlight || await getAll(TYPE).catch(() => []);

  for (const r of rows) {
    const p = r.payload || {};
    let endpoint = p.endpoint || '';
    let body = p.body;
    let touched = false;

    if (endpoint.includes(tempId)) {
      endpoint = endpoint.split(tempId).join(String(realId));
      touched = true;
    }
    if (body && typeof body === 'object') {
      const next = { ...body };
      for (const [k, v] of Object.entries(next)) {
        if (typeof v === 'string' && v === tempId) { next[k] = realId; touched = true; }
      }
      if (touched) body = next;
    }

    if (touched) {
      // Mutated in place as well as persisted: `r` may be an element of the
      // array the flush loop is iterating.
      r.payload = { ...p, endpoint, body };
      try { await update({ ...r }); } catch (_) {}
    }
  }
}

/** The id the server gave a create, wherever it put it. */
function createdIdFrom(response) {
  if (!response || typeof response !== 'object') return null;
  return response.id
    ?? response._id
    ?? response.data?.id
    ?? response.record?.id
    ?? response.created?.id
    ?? null;
}

function isNetworkError(err) {
  if (!err) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err.response) return false; // we got an HTTP response, so the network worked
  const msg = String(err.message || '').toLowerCase();
  return msg.includes('network request failed') || msg.includes('network error') || msg.includes('failed to fetch');
}

async function deliver(record) {
  const { endpoint, method, body } = record.payload || {};
  return fetchJson(endpoint, {
    method: method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Stable across retries — this is what makes the replay safe.
      'Idempotency-Key': record.idempotencyKey,
      // Replay under the scope the write was created in, not the current one.
      ...(record.scope || {}),
    },
    body: JSON.stringify(body ?? {}),
  });
}

/**
 * Replay this user's queued writes, oldest first.
 *
 * Stops at the first network error (still offline) and keeps the rest for later.
 * Returns { sent, failed, remaining }.
 */
export async function flushQueuedWrites() {
  let rows = [];
  try {
    rows = await getAll(TYPE);
  } catch (_) {
    return { sent: 0, failed: 0, remaining: 0 };
  }

  const me = currentIdentityId();
  if (!me) {
    // Logged out — nothing may be sent on anyone's behalf.
    return { sent: 0, failed: 0, remaining: rows.length };
  }

  let sent = 0;
  let failed = 0;

  for (const record of rows) {
    // Queued before owner tagging existed: ownership can't be established, so the
    // write can't be attributed to anyone. Discard rather than post as the wrong user.
    if (!record.ownerId) {
      try { await remove(record.id); } catch (_) {}
      continue;
    }
    if (record.ownerId !== me) continue; // another user's queue — leave it for them

    // Attendance is no longer queued, but registers captured by an older build
    // may still be sitting here, and they can never be delivered: the server
    // requires the date to be today AND the clock to be inside the lesson plus
    // grace, both re-checked on arrival. Replaying one returns 409 — which the
    // handler below reads as "still processing, keep it" — so it would be
    // retried on every flush forever, never sent and never dropped.
    //
    // Discarding is the truthful outcome. The register was never filed, and
    // keeping it queued only delays the teacher finding that out.
    if (record.kind === WRITE_KINDS.ATTENDANCE) {
      try { await remove(record.id); } catch (_) {}
      failed += 1;
      continue;
    }

    // Still addressed to a temporary id: the create it depends on has not been
    // delivered yet. Sending now would 404. It gets rewritten when that create
    // lands, so leave it and come back.
    if (isOfflineId(idInPath(record.payload?.endpoint || ''))) continue;

    try {
      const response = await deliver(record);

      // A create that succeeded may be referenced by writes still queued under
      // its temporary id. Rewrite them before moving on.
      const method = String(record.payload?.method || 'POST').toUpperCase();
      if (method === 'POST' && !idInPath(record.payload?.endpoint || '')) {
        const realId = createdIdFrom(response);
        if (realId != null) {
          await remapOfflineId(`${OFFLINE_ID_PREFIX}${record.id}`, realId, rows);
        }
      }

      await remove(record.id);
      sent += 1;
    } catch (err) {
      if (isNetworkError(err)) break; // still offline; retry the rest next time

      const status = err?.response?.status;

      // The server is still processing an identical replay. Leave it queued and
      // come back to it — this is not a failure.
      if (status === 409) continue;

      // 422 means this key was already used with a different body: the record is
      // unsendable as-is and retrying can never succeed. Drop it.
      if (status === 422) {
        try { await remove(record.id); } catch (_) {}
        failed += 1;
        continue;
      }

      const tries = (record.tries || 0) + 1;
      failed += 1;
      try {
        if (tries >= MAX_TRIES) {
          await remove(record.id);
        } else {
          await update({ ...record, tries, lastError: err?.userMessage || err?.message || 'send failed' });
        }
      } catch (_) {}
      // Continue rather than letting one bad record block the whole queue.
    }
  }

  let remaining = 0;
  try {
    remaining = (await getAll(TYPE)).filter((r) => r.ownerId === me).length;
  } catch (_) {}

  return { sent, failed, remaining };
}
